import type { IMessageBus, MessageResponse } from "@brains/messaging-service";
import { PermissionService, type UserPermissionLevel } from "@brains/templates";
import { type Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import {
  McpServer,
  ResourceTemplate as MCPResourceTemplate,
} from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolAnnotations } from "@modelcontextprotocol/sdk/types.js";
import type {
  MCPProtocolMode,
  Prompt,
  Resource,
  ResourceTemplate,
  Tool,
} from "./types";
import { normalizeToolExecutionMessageResponse } from "./tool-response-validation";

const MCP_SERVER_INFO = {
  name: "brain-mcp",
  version: "1.0.0",
};

const DEFAULT_TOOL_VISIBILITY: UserPermissionLevel = "anchor";
const RESOURCE_VISIBILITY: UserPermissionLevel = "anchor";
const DEFAULT_PROMPT_VISIBILITY: UserPermissionLevel = "anchor";

export interface RegisteredTool {
  pluginId: string;
  tool: Tool;
}

export interface RegisteredResource {
  pluginId: string;
  resource: Resource;
}

export interface RegisteredTemplate {
  pluginId: string;
  template: ResourceTemplate;
}

export interface RegisteredPrompt {
  pluginId: string;
  prompt: Prompt;
}

export function createMcpServerInstance(): McpServer {
  return new McpServer(MCP_SERVER_INFO);
}

export function canExposeTool(
  permissionLevel: UserPermissionLevel,
  tool: Tool,
): boolean {
  return PermissionService.hasPermission(
    permissionLevel,
    tool.visibility ?? DEFAULT_TOOL_VISIBILITY,
  );
}

export function canExposeToolOnProtocol(
  permissionLevel: UserPermissionLevel,
  tool: Tool,
  mode: MCPProtocolMode,
): boolean {
  if (!canExposeTool(permissionLevel, tool)) {
    return false;
  }

  if (mode === "debug") {
    return true;
  }

  return (
    isReadOnlyTool(tool) || tool.name === "chat" || tool.name === "confirm"
  );
}

export function canExposeResource(
  permissionLevel: UserPermissionLevel,
): boolean {
  return PermissionService.hasPermission(permissionLevel, RESOURCE_VISIBILITY);
}

/**
 * Resource templates expose entity listing/completion that can leak entity
 * existence even when handler output is filtered by visibility. Pin them to
 * the same anchor-only policy as plain resources.
 */
export function canExposeResourceTemplate(
  permissionLevel: UserPermissionLevel,
): boolean {
  return PermissionService.hasPermission(permissionLevel, RESOURCE_VISIBILITY);
}

/**
 * Prompts ship as anchor-only by default because their bodies can reference
 * restricted workflows, entity names, or operator instructions. Plugins can
 * opt a prompt down to "trusted" / "public" if its template is safe to share.
 */
export function canExposePrompt(
  permissionLevel: UserPermissionLevel,
  prompt: Prompt,
): boolean {
  return PermissionService.hasPermission(
    permissionLevel,
    prompt.visibility ?? DEFAULT_PROMPT_VISIBILITY,
  );
}

export function filterToolsForPermission(
  tools: RegisteredTool[],
  userLevel: UserPermissionLevel,
): RegisteredTool[] {
  return tools.filter(({ tool }) => canExposeTool(userLevel, tool));
}

export function getToolAnnotations(tool: Tool): ToolAnnotations | undefined {
  if (tool.annotations) {
    return tool.annotations;
  }

  if (tool.sideEffects === "none") {
    return {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    };
  }

  if (tool.sideEffects === "writes") {
    return {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: false,
    };
  }

  if (tool.sideEffects === "external") {
    return {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: true,
    };
  }

  return undefined;
}

export function isReadOnlyTool(tool: Tool): boolean {
  return getToolAnnotations(tool)?.readOnlyHint === true;
}

export function serializeMessageResponse(response: MessageResponse): string {
  if ("success" in response && !response.success) {
    throw new Error(response.error ?? "Operation failed");
  }
  return JSON.stringify("data" in response ? response.data : response, null, 2);
}

export function registerToolOnServer(
  server: McpServer,
  pluginId: string,
  tool: Tool,
  messageBus: IMessageBus,
  logger: Logger,
  permissionLevel: UserPermissionLevel,
): void {
  server.tool(
    tool.name,
    tool.description,
    tool.inputSchema,
    getToolAnnotations(tool) ?? {},
    async (params, extra) => {
      const interfaceType = extra._meta?.["interfaceType"] ?? "mcp";
      const userId = extra._meta?.["userId"] ?? "mcp-user";
      const conversationId = extra._meta?.["conversationId"];
      const channelId = extra._meta?.["channelId"];
      const channelName = extra._meta?.["channelName"];
      const progressToken = extra._meta?.progressToken;

      logger.debug("MCP client metadata", {
        tool: tool.name,
        pluginId,
        interfaceType,
        userId,
        conversationId,
        channelId,
        channelName,
        progressToken,
        userPermissionLevel: permissionLevel,
      });

      try {
        const response = await messageBus.send({
          type: `plugin:${pluginId}:tool:execute`,
          payload: {
            toolName: tool.name,
            args: params,
            progressToken,
            hasProgress: progressToken !== undefined,
            interfaceType,
            userId,
            conversationId,
            channelId,
            channelName,
            userPermissionLevel: permissionLevel,
          },
          sender: "MCPService",
        });
        const normalizedResponse = normalizeToolExecutionMessageResponse(
          response,
          {
            pluginId,
            toolName: tool.name,
            logger,
          },
        );

        return {
          content: [
            {
              type: "text" as const,
              text: serializeMessageResponse(normalizedResponse),
            },
          ],
        };
      } catch (error) {
        logger.error(`Tool execution error for ${tool.name}`, error);
        throw error;
      }
    },
  );
}

export function registerResourceOnServer(
  server: McpServer,
  resource: Resource,
): void {
  server.resource(
    resource.name,
    resource.uri,
    { description: resource.description, mimeType: resource.mimeType },
    async () => resource.handler(),
  );
}

export function registerResourceTemplateOnServer(
  server: McpServer,
  template: ResourceTemplate,
): void {
  const listFn = template.list;

  const sdkTemplate = new MCPResourceTemplate(template.uriTemplate, {
    list: listFn
      ? async (): Promise<{
          resources: Array<{ uri: string; name: string }>;
        }> => ({
          resources: (await listFn()).map((r) => ({
            uri: r.uri,
            name: r.name,
          })),
        })
      : undefined,
    ...(template.complete && {
      complete: Object.fromEntries(
        Object.entries(template.complete).map(([k, fn]) => [
          k,
          (
            value: string,
            context?: { arguments?: Record<string, string> },
          ): string[] | Promise<string[]> => fn(value, context),
        ]),
      ),
    }),
  });

  server.registerResource(
    template.name,
    sdkTemplate,
    { description: template.description, mimeType: template.mimeType },
    async (_uri, vars) => {
      const flatVars: Record<string, string> = {};
      for (const [k, v] of Object.entries(vars)) {
        flatVars[k] = Array.isArray(v) ? (v[0] ?? "") : v;
      }
      return template.handler(flatVars);
    },
  );
}

export function registerPromptOnServer(
  server: McpServer,
  prompt: Prompt,
): void {
  const argsSchema = Object.fromEntries(
    Object.entries(prompt.args).map(([key, arg]) => [
      key,
      arg.required
        ? z.string().describe(arg.description)
        : z.string().optional().describe(arg.description),
    ]),
  );

  server.prompt(
    prompt.name,
    prompt.description ?? "Prompt",
    argsSchema,
    async (args) => prompt.handler(args as Record<string, string>),
  );
}
